import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { Company, InvoiceItem } from '../types';
import { MainContainer } from '../cardStyles';
import TotaisSection from '../Sections/TotaisSection';
import EmissaoSection from '../Sections/EmissaoSection';
import FreteSection from '../Sections/FreteSection';
import InfoAdicionaisSection from '../Sections/InfoAdicionaisSection';
import PagamentoSection from '../Sections/PagamentoSection';
import DestinatarioSection from '../Sections/DestinatarioSection';
import IdentificacaoSection, { IdentificacaoValues } from '../Sections/IdentificacaoSection';
import ItensSection from '../Sections/ItensSection';
import { useEmissionPolling } from '../useEmissionPolling';

const EMPTY_IDENT: IdentificacaoValues = {
  companyEmissorId: '',
  naturezaOperacao: '',
  tipoOperacao: '',
  finalidade: '',
};

const ViewInvoice = () => {
  const { id: invoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);

  const [invoice, setInvoice] = useState<any>(null);
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [selectedDestinatario, setSelectedDestinatario] = useState<{ company: Company } | null>(null);

  const formatBrlNumber = (val: any) =>
    val ? Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const response = await ApiService.fiscal.getInvoice(Number(invoiceId));
      const data = response.data;
      if (!data) return;

      setInvoice(data);
      if (data.CompanyCliente) {
        setSelectedDestinatario({ company: data.CompanyCliente });
      }
      if (Array.isArray(data.InvoiceProduct)) {
        const mapped = data.InvoiceProduct.map((p: any) => ({
          id: p.id || p.orderItemId,
          codigo: p.codigoProduto || `ITEM-${p.orderItemId || p.id}`,
          descricao: p.descricaoProduto || 'Produto Sem Descrição',
          ncmId: p.ncmId || null,
          cfopId: null,
          cfop: p.cfop || '',
          ncmCode: p.Ncm?.ncm || '',
          cst: p.cstICMS || '',
          cstPisCofins: p.cstPIS_COFINS || '',
          cstIbsCbs: p.cstIBS_CBS || '',
          classificacao: p.classificacaoTributaria || '',
          unidade: 'un',
          quantidade: p.quantidade || 0,
          vlUnit: formatBrlNumber(p.valorUnitario) || '0,00',
          vlTotal: formatBrlNumber(p.valorTotal) || '0,00',
          bcIcms: formatBrlNumber(p.baseCalculoICMS || p.valorTotal),
          vlIcms: formatBrlNumber(p.valorICMS),
          vlIpi: formatBrlNumber(p.valorIPI),
          vlIbs: formatBrlNumber(p.valorIBS),
          vlCbs: formatBrlNumber(p.valorCBS),
          aliqIcms: p.aliquotaICMS ?? '',
          aliqIpi: p.aliquotaIPI ?? '',
          _lastLookupHash: '',
        }));
        setItems(mapped);
      }
    } catch (error) {
      console.error('Erro ao carregar nota fiscal:', error);
      setFeedback({ type: 'error', message: 'Erro ao carregar a nota fiscal.', isOpen: true, statusOk: false });
    }
  }, [invoiceId, setFeedback]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  // Sincroniza com o Nota Gateway (consultarStatus grava EMITIDA/NEGADA) e depois
  // recarrega para refletir. Só reler (loadInvoice) deixaria a nota presa em EMITINDO.
  const syncStatus = useCallback(async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.nfeStatus(Number(invoiceId));
    } catch (error) {
      console.error('Erro ao consultar status da NF-e:', error);
    }
    await loadInvoice();
  }, [invoiceId, loadInvoice]);

  useEmissionPolling({ isEmitindo: invoice?.status === 'EMITINDO', onTick: syncStatus });

  const handleCancelar = async () => {
    if (!invoiceId) return;
    try {
      await ApiService.fiscal.cancelar(Number(invoiceId));
      setFeedback({ message: 'Cancelamento solicitado.', isOpen: true, statusOk: true });
      loadInvoice();
    } catch (error: any) {
      setFeedback({ message: error?.response?.data?.message || 'Erro ao cancelar a nota.', isOpen: true, statusOk: false });
    }
  };

  return (
    <MainContainer style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px' }}>
      {/* COLUNA DA ESQUERDA */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <DestinatarioSection
          companies={[]}
          selectedDestinatario={selectedDestinatario}
          onSelect={() => {}}
          readOnly
        />
        <ItensSection
          items={items}
          setItems={setItems}
          cfops={[]}
          cfopsPerItem={new Map()}
          destStateId={null}
          isEmitida
          onRemove={() => {}}
          fetchNcmsForItem={async () => {}}
          ncmOptions={[]}
        />
        <PagamentoSection invoice={invoice} readOnly />
        <FreteSection invoice={invoice} readOnly />
        <InfoAdicionaisSection invoice={invoice} readOnly />
      </div>

      {/* COLUNA DA DIREITA */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', position: 'sticky', top: '0', alignSelf: 'start' }}>
        <EmissaoSection
          invoice={invoice}
          invoiceId={invoiceId ?? null}
          onEmitir={() => {}}
          onCancelar={handleCancelar}
          onVoltar={() => navigate('/panel/invoice')}
          readOnly
        />
        <IdentificacaoSection
          emissoras={[]}
          invoice={invoice}
          value={EMPTY_IDENT}
          onChange={() => {}}
          readOnly
        />
        <TotaisSection items={items} />
      </div>
    </MainContainer>
  );
};

export default ViewInvoice;
